<script>
/**
 * @component ImageCatalog
 * @description ABM del catálogo de órdenes de imagen.
 * Lista con filtro por categoría y búsqueda, CRUD completo.
 * @example <ImageCatalog />
 */
import imageCatalogService from "@/services/image-catalog";
import Spinner from "@/components/ui/Spinner";
import debounce from "debounce";
import { mapGetters } from "vuex";

const DEBOUNCE_MS = 300;

export default {
  name: "ImageCatalog",
  components: { Spinner },

  data() {
    return {
      isLoading: false,
      searchInput: "",
      filterCategory: "",
      images: [],
      categories: [],

      isFormVisible: false,
      isFormLoading: false,
      formMode: "create",
      form: this.emptyForm(),
    };
  },

  computed: {
    ...mapGetters(["isAdmin"]),

    /** @returns {string} Título del dialog */
    formTitle() {
      return this.formMode === "create" ? "Nueva imagen" : "Editar imagen";
    },
  },

  created() {
    this.fetchImages();
    this.fetchCategories();
  },

  methods: {
    /** @returns {Object} Formulario vacío */
    emptyForm() {
      return { code: "", description: "", category: "" };
    },

    /**
     * @description Carga imágenes con filtros
     */
    async fetchImages() {
      this.isLoading = true;
      try {
        const params = {};
        if (this.searchInput) params.search = this.searchInput;
        if (this.filterCategory) params.category = this.filterCategory;
        this.images = await imageCatalogService.getAll(params);
      } catch (error) {
        this.$message.error("Error al cargar imágenes");
        console.error(error);
      } finally {
        this.isLoading = false;
      }
    },

    /**
     * @description Carga las categorías disponibles para el filtro
     */
    async fetchCategories() {
      try {
        this.categories = await imageCatalogService.getCategories();
      } catch (error) {
        console.error("Error al cargar categorías", error);
      }
    },

    debounceSearch: debounce(function () {
      this.fetchImages();
    }, DEBOUNCE_MS),

    onFilterChange() {
      this.fetchImages();
    },

    openCreate() {
      this.formMode = "create";
      this.form = this.emptyForm();
      this.isFormVisible = true;
    },

    openEdit(image) {
      this.formMode = "edit";
      this.form = {
        _id: image._id,
        code: image.code || "",
        description: image.description || "",
        category: image.category || "",
      };
      this.isFormVisible = true;
    },

    /**
     * @description Guarda una imagen (crear o editar)
     */
    async saveForm() {
      if (!this.form.code || !this.form.description || !this.form.category) {
        this.$message.warning("Código, descripción y categoría son requeridos");
        return;
      }
      this.isFormLoading = true;
      try {
        if (this.formMode === "create") {
          await imageCatalogService.create(this.form);
          this.$message.success("Imagen creada");
        } else {
          await imageCatalogService.update(this.form._id, this.form);
          this.$message.success("Imagen actualizada");
        }
        this.isFormVisible = false;
        this.fetchImages();
        this.fetchCategories();
      } catch (error) {
        const msg = error.response?.data?.error || "Error al guardar";
        this.$message.error(msg);
      } finally {
        this.isFormLoading = false;
      }
    },

    /**
     * @description Elimina una imagen previa confirmación
     * @param {Object} image
     */
    async deleteImage(image) {
      try {
        await this.$confirm(
          `¿Eliminar "${image.description}" (${image.code})?`,
          "Eliminar",
          {
            confirmButtonText: "Aceptar",
            cancelButtonText: "Cancelar",
            type: "warning",
          }
        );
        await imageCatalogService.delete(image._id);
        this.$message.success("Imagen eliminada");
        this.fetchImages();
      } catch {
        // Cancelado
      }
    },
  },
};
</script>

<template lang="pug">
  section
    header.headline
      .headline__title Imágenes
      .headline__actions
        el-input.search__input(
          prefix-icon="el-icon-search"
          v-model="searchInput"
          @input="debounceSearch"
          placeholder="Buscar por código o descripción..."
          clearable
          size="small"
        )
        el-select(
          v-model="filterCategory"
          placeholder="Categoría"
          clearable
          @change="onFilterChange"
          size="small"
        )
          el-option(
            v-for="cat in categories"
            :key="cat"
            :label="cat"
            :value="cat"
          )
        .button.button--blue(@click="openCreate" v-if="isAdmin")
          micon(name="add_circle_outline").button__icon
          span.button__text + Nueva imagen

    .box.box--with-subnav
      .box__content--stretch
        table
          thead
            tr
              th Código
              th Descripción
              th Categoría
              th Acciones
          tbody
            spinner(v-if="isLoading")
            tr(
              v-else
              v-for="image in images"
              :key="image._id"
              @click="openEdit(image)"
            )
              td {{ image.code }}
              td {{ image.description }}
              td {{ image.category }}
              td.actions
                .actions-container(@click.stop)
                  button.button.button--action(@click="openEdit(image)")
                    micon(name="edit")
                  button.button.button--action(@click="deleteImage(image)" v-if="isAdmin")
                    micon(name="delete_forever")

    //- Dialog crear/editar
    el-dialog(:title="formTitle" :visible.sync="isFormVisible" width="500px")
      el-form(label-position="top" size="small")
        el-row(:gutter="16")
          el-col(:span="8")
            el-form-item(label="Código")
              el-input(v-model="form.code" placeholder="Ej: 1260301")
          el-col(:span="16")
            el-form-item(label="Categoría")
              el-select(v-model="form.category" placeholder="Seleccionar categoría" style="width:100%")
                el-option(
                  v-for="cat in categories"
                  :key="cat"
                  :label="cat"
                  :value="cat"
                )
        el-form-item(label="Descripción")
          el-input(v-model="form.description" placeholder="Nombre del estudio de imagen")
      template(#footer)
        el-button(@click="isFormVisible = false") Cancelar
        el-button(type="primary" @click="saveForm" :loading="isFormLoading") Guardar
</template>

<style lang="scss" scoped>
.headline {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 16px;
}

.headline__title {
  font-size: 20px;
  font-weight: 700;
  color: $navy;
}

.headline__actions {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}

.search__input {
  width: 280px;
}

.el-select {
  width: 160px;
}

tbody td {
  cursor: pointer;
}
</style>
